import { Table } from 'components/atoms/Table';
import CustomLoader from 'components/molecules/CustomLoader.component';
import SimpleError from 'components/molecules/SimpleError.component';
import Papa, { type ParseConfig } from 'papaparse';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { BankTransactionsCacheKeys } from 'types/cacheKeys.types';

const detectDelimiter = (content: string): string => {
  const firstLine =
    content.split('\n').find((line) => line.trim().length > 0) || '';

  const semicolonCount = (firstLine.match(/;/g) || []).length;

  return semicolonCount > 0 ? ';' : ',';
};

const fetchCSVData = (url: string) =>
  new Promise((resolve, reject) => {
    fetch(url)
      .then((response) => response.text())
      .then((content) => {
        const delimiter = detectDelimiter(content);

        const config: ParseConfig = {
          header: true,
          delimiter,
          skipEmptyLines: 'greedy',
          transformHeader: (header) => header.trim(),
          transform: (value) => {
            if (typeof value === 'string') {
              const trimmed = value.trim();
              if (trimmed === '') return null;

              // Handle monetary values with comma as decimal separator
              if (/^-?\d+,\d{2}$/.test(trimmed)) {
                return parseFloat(trimmed.replace(',', '.'));
              }

              return trimmed;
            }
            return value;
          },
          complete: (response) => {
            if (response.errors && response.errors.length > 0) {
              const hasNonFieldMismatchError = response.errors.some(
                (error) => error.type !== 'FieldMismatch',
              );

              if (hasNonFieldMismatchError) {
                reject(response.errors[0]);
                return;
              }
            }

            const filteredData = response.data.filter((row: any) => {
              const nonEmptyFields = Object.values(row).filter(
                (value) => value !== null && value !== '',
              ).length;

              return nonEmptyFields >= 3;
            });

            resolve(filteredData);
          },
          dynamicTyping: true,
          comments: false,
          quoteChar: '"',
          escapeChar: '"',
        };

        Papa.parse(content, config);
      })
      .catch(reject);
  });

const CSVViewer: FC<{ url: string }> = ({ url }) => {
  const { t } = useTranslation();

  const csvQuery = useQuery(
    [BankTransactionsCacheKeys.CreditCardStatementCSVFileData, url],
    () => fetchCSVData(url),
  );

  if (csvQuery.isLoading || csvQuery.isRefetching)
    return (
      <div className="h-screen flex items-center justify-center">
        <CustomLoader />
      </div>
    );

  if (
    csvQuery.isError ||
    !Array.isArray(csvQuery.data) ||
    !csvQuery.data?.length
  )
    return (
      <div className="h-screen flex items-center justify-center">
        <SimpleError
          messageClassName="!text-white"
          message={t('csv_viewer.invalid_file.message')}
          onRetry={() => csvQuery.refetch()}
        />
      </div>
    );

  const tableColumns = Object.keys(csvQuery.data?.[0] || {}) || [];

  return (
    <Table className="overflow-scroll">
      <Table.Head>
        {tableColumns?.map((column) => (
          <Table.Cell
            key={column}
            className="min-w-[240px] border-l text-sm !px-3 !py-4 bg-primary-25 font-semibold"
          >
            {column}
          </Table.Cell>
        ))}
      </Table.Head>

      {csvQuery.data.map((entry, index) => (
        <Table.Row key={index} className="hover:bg-gray-50">
          {tableColumns?.map((column) => {
            const value = entry[column];

            return (
              <Table.Cell key={column} className="border-l text-xs !px-3 !py-2">
                {Array.isArray(value)
                  ? value.join(', ')
                  : typeof value === 'string'
                  ? value?.trim()
                  : value || '-'}
              </Table.Cell>
            );
          })}
        </Table.Row>
      ))}
    </Table>
  );
};

export default CSVViewer;
